import axios from 'axios';

// Note: With React Router v6+, we can't use history.push directly outside of components
// Instead, we'll need to handle 403 errors at the component level or use a different approach
// For now, we'll throw an error that components can catch and handle

// Helper to get JWT from sessionStorage (via Zustand store)
const getJwtFromStorage = (): string | null => {
  try {
    const storedState = sessionStorage.getItem('aft-cms-storage');
    if (storedState) {
      const parsed = JSON.parse(storedState);
      return parsed.state?.user?.jwtToken || null;
    }
  } catch (e) {
    console.error('Error reading JWT from storage:', e);
  }
  return null;
};

// Helper to get refresh token from sessionStorage (via Zustand store)
const getRefreshTokenFromStorage = (): string | null => {
  try {
    const storedState = sessionStorage.getItem('aft-cms-storage');
    if (storedState) {
      const parsed = JSON.parse(storedState);
      return parsed.state?.user?.refreshToken || null;
    }
  } catch (e) {
    console.error('Error reading refresh token from storage:', e);
  }
  return null;
};

// Helper to update JWT in sessionStorage (via Zustand store)
const updateJwtInStorage = (newJwt: string): void => {
  try {
    const storedState = sessionStorage.getItem('aft-cms-storage');
    if (storedState) {
      const parsed = JSON.parse(storedState);
      if (parsed.state?.user) {
        parsed.state.user.jwtToken = newJwt;
        sessionStorage.setItem('aft-cms-storage', JSON.stringify(parsed));
      }
    }
  } catch (e) {
    console.error('Error updating JWT in storage:', e);
  }
};

export function configureInterceptors(refreshToken: string) {
  // Add request interceptor to include JWT in Authorization header
  axios.interceptors.request.use(
    function (config) {
      const jwt = getJwtFromStorage();
      if (jwt && config.headers) {
        config.headers.Authorization = `Bearer ${jwt}`;
      }
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for error handling and token refresh
  axios.interceptors.response.use(
    function (response) {
      // add a safety net here for the response
      if (response) {
        return response;
      } else {
        throw new Error('Response was not a valid object.');
      }
    },
    async function (error) {
      // check if user's action cancelled the request
      if (axios.isCancel(error)) {
        throw new axios.Cancel(error.message);
      }

      // Do something with response error
      const { status, config, data } = error.response || {};
      
      // Get refresh token from sessionStorage (tab-specific)
      const tabRefreshToken = getRefreshTokenFromStorage();

      // this has to stay at a 401, if it is anything other than that, it can cause a huge security vulnerability
      if (status === 401 && !data?.forLogin && tabRefreshToken) {
        if (config?.url === '/api/Security/refreshToken') {
          // Refresh token itself failed - clear auth and redirect to login
          sessionStorage.removeItem('aft-cms-storage');
          window.location.href = '/landing';
          return Promise.reject(error);
        } else {
          try {
            const response = await axios({
              method: 'POST',
              url: '/api/Security/refreshToken',
              headers: {
                'Content-Type': 'application/json',
              },
              data: JSON.stringify({ RefreshToken: tabRefreshToken }),
            });
            if (response.status === 200 && response.data?.jwtToken) {
              // Update JWT in storage
              updateJwtInStorage(response.data.jwtToken);
              // Retry the original request with new JWT
              if (config.headers) {
                config.headers.Authorization = `Bearer ${response.data.jwtToken}`;
              }
              return axios(config);
            }
            return Promise.reject(response);
          } catch (refreshError) {
            // Refresh token request failed - clear auth and redirect to login
            sessionStorage.removeItem('aft-cms-storage');
            window.location.href = '/landing';
            return Promise.reject(refreshError);
          }
        }
      } else if (status === 401 && !data?.forLogin) {
        // 401 without refresh token - clear auth and redirect to login
        sessionStorage.removeItem('aft-cms-storage');
        window.location.href = '/landing';
        return Promise.reject(error);
      } else if (status === 403) {
        // Handle 403 by redirecting - this will be caught by error boundaries or component error handling
        window.location.href = '/not-authorized';
      }
      return Promise.reject(error);
    }
  );
}
